import React, { useState } from "react";
import { Modal, Row, Col, Button, Badge, Spinner } from "react-bootstrap";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaUserSlash,
  FaClock,
} from "react-icons/fa";
import CoachPrivileges from "./CoachPrivileges";
import { updateCoachPrivileges } from "../../../../backend/firebaseFunctions";

const CoachDetailsModal = ({
  show,
  onHide,
  coach,
  onDisableCoach,
  loading,
  hasEditPrivilege,
  setCoaches,
  showDisableConfirmModal,
  setShowDisableConfirmModal,
}) => {
  const [savingPrivileges, setSavingPrivileges] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  if (!coach) return null;

  const handleDisableClick = () => {
    setShowDisableConfirmModal(true);
  };

  const handleConfirmDisable = () => {
    setShowDisableConfirmModal(false);
    onDisableCoach(coach);
  };

  const handleSavePrivileges = async (privileges) => {
    try {
      setSavingPrivileges(true);
      await updateCoachPrivileges(
        coach.userData.userOrganization.organizationOwnerID,
        coach.userData.userId,
        privileges
      );
      setCoaches((prevCoaches) =>
        prevCoaches.map((c) =>
          c.userData.userId === coach.userData.userId
            ? { ...c, accessPrivilege: privileges }
            : c
        )
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error saving privileges:", error);
      throw error;
    } finally {
      setSavingPrivileges(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm("יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?")
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  const getLastLoginColor = (lastLoginAt) => {
    if (!lastLoginAt) return "text-muted";
    const lastLogin = lastLoginAt.toDate
      ? lastLoginAt.toDate()
      : new Date(lastLoginAt);
    const now = new Date();
    const daysDiff = (now - lastLogin) / (1000 * 60 * 60 * 24);

    if (daysDiff <= 3) return "text-success";
    if (daysDiff <= 5) return "text-warning";
    return "text-danger";
  };

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return "לא התחבר מעולם";
    const date = lastLoginAt.toDate
      ? lastLoginAt.toDate()
      : new Date(lastLoginAt);
    const now = new Date();
    const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    const daysText = daysDiff === 0 ? "היום" : `לפני ${daysDiff} ימים`;
    return `${date.toLocaleDateString("he-IL")} ${date.toLocaleTimeString(
      "he-IL",
      { hour: "2-digit", minute: "2-digit" }
    )} (${daysText})`;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        className="coach-modal"
        backdropClassName="bg-dark bg-opacity-50"
        dir="rtl"
        style={{ zIndex: showDisableConfirmModal ? 1050 : 1051 }}
      >
        <Modal.Header className="bg-primary text-white border-0">
          <div className="d-flex justify-content-between align-items-center" style={{ width: "100%" }}>
            <div className="">
              <h2 className="mb-0 fw-bold">
                {coach?.userData?.userName || "מאמן"}
              </h2>
            </div>
            <Badge bg="light" text="dark" className="fs-6">
              <FaUser className="me-1" />
            </Badge>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          <Row className="g-4">
            <Col xs={12} md={6}>
              <div className="d-flex align-items-center gap-3">
                <FaPhone className="text-primary" size={20} />
                <div>
                  <h6 className="mb-0 text-muted">טלפון</h6>
                  <p className="mb-0">
                    {coach.userData?.userPhoneNumber || "לא צוין"}
                  </p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="d-flex align-items-center gap-3">
                <FaEnvelope className="text-primary" size={20} />
                <div>
                  <h6 className="mb-0 text-muted">אימייל</h6>
                  <p className="mb-0">{coach.userData?.userEmailAddress}</p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="d-flex align-items-center gap-3">
                <FaClock
                  className={getLastLoginColor(coach.userData?.lastLoginAt)}
                  size={20}
                />
                <div>
                  <h6 className="mb-0 text-muted">התחברות אחרונה למערכת</h6>
                  <p className="mb-0">
                    {formatLastLogin(coach.userData?.lastLoginAt)}
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {hasEditPrivilege && (
            <CoachPrivileges
              coach={coach}
              onSave={handleSavePrivileges}
              loading={savingPrivileges}
              onChanges={setHasUnsavedChanges}
            />
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          {hasEditPrivilege && (
            <Button
              variant="danger"
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={handleDisableClick}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaUserSlash />
                  השבת מאמן
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        show={showDisableConfirmModal}
        onHide={() => setShowDisableConfirmModal(false)}
        centered
        dir="rtl"
        backdrop="static"
        style={{ zIndex: 1052 }}
      >
        <Modal.Header className="d-flex justify-content-between align-items-center">
          <Modal.Title className="mb-0">אישור השבתת מאמן</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          האם אתה בטוח שברצונך להשבית את המאמן {coach?.userData?.userName}?
        </Modal.Body>
        <Modal.Footer
          style={{
            justifyContent: "space-between",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowDisableConfirmModal(false)}
          >
            ביטול
          </Button>
          <Button variant="danger" onClick={handleConfirmDisable}>
            השבת מאמן
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CoachDetailsModal;
